import { useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { toast } from '@/hooks/use-toast'
import { timelineService } from '@/services/timeline'
import { CreateTimelinePost } from '@/types/post'
import { useTimeline } from '@/hooks/use-timeline'
import { useTerritories } from '@/hooks/use-territories'
import { useTrends } from '@/hooks/use-trends'
import { useData } from '@/contexts/data'
import { postFormSchema, type PostFormData, type CreatePostModalProps } from './types'
import { DEFAULT_FORM_VALUES } from './constants'
import {
  CreatePostModalHeader,
  FormTitleTypeRow,
  FormContentField,
  FormUrlField,
  FormTagsSection,
  FormSubmitButton,
} from './components'

export default function CreatePostModal({ open, onOpenChange, onSuccess }: CreatePostModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { createPost } = useTimeline({ offset: 0, limit: 10 })
  const { territories, loading: territoriesLoading } = useTerritories()
  const { topics, topicsLoading, contexts, contextsLoading } = useData()
  const { trends, loading: trendsLoading } = useTrends()

  const methods = useForm<PostFormData>({
    mode: 'onChange',
    resolver: zodResolver(postFormSchema),
    defaultValues: DEFAULT_FORM_VALUES,
  })

  const handleSubmit = async (data: PostFormData) => {
    try {
      setIsSubmitting(true)
      const postData: CreateTimelinePost = {
        title: data.title,
        content: data.content,
        engagement: 0,
        type: data.type,
        url: data.url,
        trends: data.trends?.length ? data.trends : undefined,
        territories: data.territories?.length ? data.territories : undefined,
        contexts: data.contexts?.length ? data.contexts : undefined,
        topics: data.topics?.length ? data.topics : undefined,
      }
      const newPost = await createPost(postData)
      if (newPost?.id) {
        try {
          await timelineService.approve(newPost.id)
        } catch {
          toast({
            variant: 'error',
            title: 'Erro ao aprovar post',
            description: 'O post foi criado mas não pôde ser aprovado automaticamente.',
          })
        }
      }
      methods.reset()
      onOpenChange(false)
      onSuccess?.()
    } finally {
      setIsSubmitting(false)
    }
  }

  const trendsItems = trends?.map((t) => ({ id: t.id, name: t.name })) ?? []
  const territoriesItems = territories?.map((t) => ({ id: t.id, name: t.name })) ?? []
  const contextsItems = contexts?.map((c) => ({ id: c.id, name: c.name })) ?? []
  const topicsItems = topics?.map((t) => ({ id: t.id, name: t.name })) ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <CreatePostModalHeader />
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSubmit)} className="space-y-6">
            <FormTitleTypeRow />
            <FormContentField />
            <FormUrlField />
            <FormTagsSection
              trends={trendsItems}
              trendsLoading={trendsLoading}
              territories={territoriesItems}
              territoriesLoading={territoriesLoading}
              contexts={contextsItems}
              contextsLoading={contextsLoading}
              topics={topicsItems}
              topicsLoading={topicsLoading}
            />
            <FormSubmitButton isSubmitting={isSubmitting} />
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  )
}
